import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../../Layout/MainLayout';
import PageContainer from '../../components/layout/PageContainer';
import TrackingBanner from './sections/Shared/TrackingBanner';
import TrackingTabs from './sections/Shared/TrackingTabs';
import LiveTrackingPage from './sections/LiveTracking/LiveTrackingPage';
import FlightTrackingPage from './sections/FlightTracking/FlightTrackingPage';
import TrainTrackingPage from './sections/TrainTracking/TrainTrackingPage';
import FeatureLocked from '../../components/gating/FeatureLocked';
import { usePlanAccess } from '../../components/gating/PlanGate';
import { TRACKING_BANNERS, tabVariants } from '../../data/tracking/trackingData';

const TrackingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLocked: isLiveLocked } = usePlanAccess('pro');

  const getInitialTab = () => {
    if (location.pathname.includes('/flight-tracking')) return 'flight-tracking';
    if (location.pathname.includes('/train-tracking')) return 'train-tracking';
    return 'live-location';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());

  useEffect(() => {
    setActiveTab(getInitialTab());
  }, [location.pathname]);

  useEffect(() => {
    // Preload banner images for instant, flicker-free cross-fade transitions
    Object.values(TRACKING_BANNERS).forEach((banner) => {
      if (banner?.image) {
        const img = new Image();
        img.src = banner.image;
      }
    });
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/tracking/${tab}`);
  };

  const currentBanner = TRACKING_BANNERS[activeTab] || TRACKING_BANNERS['live-location'];

  return (
    <MainLayout activeTab="tracking">
      <PageContainer>
        {/* Banner at Top with Smooth In-Place Cross-Fade */}
        <TrackingBanner
          title={currentBanner.title}
          description={currentBanner.description}
          image={currentBanner.image}
        />

        {/* Tab Selector Below Banner */}
        <TrackingTabs activeTab={activeTab} onTabChange={handleTabChange} />

        {/* Tab Content with Smooth Animation */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} variants={tabVariants} initial="hidden" animate="visible" exit="exit">
            {activeTab === 'live-location' && (
              <div className="relative">
                <LiveTrackingPage hideHeader={true} />
                {isLiveLocked && <FeatureLocked featureKey="live-location" overlay={true} />}
              </div>
            )}

            {activeTab === 'flight-tracking' && <FlightTrackingPage hideHeader={true} />}

            {activeTab === 'train-tracking' && <TrainTrackingPage hideHeader={true} />}
          </motion.div>
        </AnimatePresence>
      </PageContainer>
    </MainLayout>
  );
};

export default TrackingPage;
